import { LitElement, html, css } from "lit";
import { customElement, property } from "lit/decorators.js";

/**
 * bn-checkbox is a token-styled checkbox carrying its own label and optional
 * hint (unlike bn-input it is not meant to be wrapped in bn-field). It mirrors
 * the state on the `checked` property and emits a composed `change`
 * CustomEvent, so pages bind it like:
 * `.checked=${x} @change=${(e) => x = e.detail.checked}`.
 */
let uid = 0;

@customElement("bn-checkbox")
export class BnCheckbox extends LitElement {
  @property({ type: Boolean }) checked = false;
  @property() label = "";
  @property() hint = "";
  @property({ type: Boolean }) disabled = false;

  /** Stable id/name for the internal control so it isn't an anonymous form field. */
  private readonly fieldId = `bn-checkbox-${++uid}`;

  static override styles = css`
    :host {
      display: block;
    }
    label {
      display: flex;
      align-items: flex-start;
      gap: var(--bino-space-sm);
      cursor: pointer;
      font-family: var(--bino-font-sans);
    }
    label.disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
    /* Visually hidden but focusable: the label forwards clicks, the box below
       renders the state, and :focus-visible styles reach it via the sibling. */
    input {
      position: absolute;
      opacity: 0;
      width: 1px;
      height: 1px;
      margin: 0;
      pointer-events: none;
    }
    .box {
      flex: none;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 1.125rem;
      height: 1.125rem;
      margin-top: 1px;
      border: 1px solid var(--bino-border-light);
      border-radius: var(--bino-radius-sm);
      background: var(--bino-surface-subtle);
      transition:
        border-color var(--bino-transition-fast),
        background var(--bino-transition-fast),
        box-shadow var(--bino-transition-fast);
    }
    label:hover:not(.disabled) .box {
      border-color: var(--bino-border-hover);
    }
    input:focus-visible + .box {
      border-color: var(--bino-primary);
      box-shadow: 0 0 0 3px var(--bino-focus-ring);
    }
    input:checked + .box {
      border-color: var(--bino-primary);
      background: var(--bino-primary);
    }
    .box svg {
      display: none;
      width: 0.75rem;
      height: 0.75rem;
      stroke: var(--bino-on-primary);
      stroke-width: 2;
      fill: none;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
    input:checked + .box svg {
      display: block;
    }
    .text {
      display: block;
    }
    .label {
      display: block;
      font-size: var(--bino-font-size-base);
      color: var(--bino-text);
    }
    .hint {
      display: block;
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      margin-top: 0.125rem;
    }
  `;

  private onChange(e: Event) {
    // The native `change` event is not composed, so it never escapes the
    // shadow root on its own — consumers only ever see the CustomEvent below
    // with the new state in `detail`.
    e.stopPropagation();
    this.checked = (e.target as HTMLInputElement).checked;
    this.dispatchEvent(
      new CustomEvent("change", { detail: { checked: this.checked }, bubbles: true, composed: true }),
    );
  }

  override render() {
    return html`
      <label class="${this.disabled ? "disabled" : ""}">
        <input
          id=${this.fieldId}
          name=${this.fieldId}
          type="checkbox"
          .checked=${this.checked}
          ?disabled=${this.disabled}
          @change=${this.onChange}
        />
        <span class="box" aria-hidden="true">
          <svg viewBox="0 0 12 12"><path d="M2.5 6.5 5 9l4.5-6"></path></svg>
        </span>
        <span class="text">
          <span class="label">${this.label}</span>
          ${this.hint ? html`<span class="hint">${this.hint}</span>` : ""}
        </span>
      </label>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bn-checkbox": BnCheckbox;
  }
}
